import { WorkflowTransitionInfoFragment } from '@spotify/confidence-graphql';

import { WorkflowInstance } from '../useGetWorkflowInstance';

export type WorkflowTransitionProgress = {
  action: string;
  currentStepDisplayName?: string;
  completedSteps: number;
  totalSteps: number;
  failureMessage?: string;
};

type WorkflowInstanceTransition = Pick<
  WorkflowInstance,
  'state' | 'pendingTransition' | 'transitionStatus'
>;

type WorkflowInfo = WorkflowTransitionInfoFragment['workflowInfo'];

const getActionDefinition = (
  workflowInfo: WorkflowInfo | undefined,
  { state, action, steps }: { state: string; action: string; steps: string[] },
) => {
  const definitions =
    workflowInfo?.states.flatMap(({ key, value }) =>
      (value?.actions ?? [])
        .filter(entry => entry.key === action)
        .map(entry => ({ state: key, definition: entry.value })),
    ) ?? [];
  const countDefinedSteps = (entry: (typeof definitions)[number]) =>
    steps.filter(step => entry.definition?.actionSteps.includes(step)).length;
  const [closestDefinition] = [...definitions]
    .filter(entry => !steps.length || countDefinedSteps(entry) > 0)
    .sort((a, b) => countDefinedSteps(b) - countDefinedSteps(a));
  return (definitions.find(entry => entry.state === state) ?? closestDefinition)
    ?.definition;
};

export const getWorkflowTransitionProgress = (
  { state, pendingTransition, transitionStatus }: WorkflowInstanceTransition,
  workflowInfo?: WorkflowInfo,
  lastStepProgress?: WorkflowTransitionProgress | null,
): WorkflowTransitionProgress | null => {
  if (
    pendingTransition &&
    transitionStatus?.completed &&
    transitionStatus.action === pendingTransition.transitionAction
  ) {
    return null;
  }

  if (!pendingTransition) {
    if (!transitionStatus?.running && !transitionStatus?.failed) return null;
    const failureMessage = transitionStatus.failed
      ? transitionStatus.failed.error || 'The transition failed'
      : undefined;
    if (lastStepProgress?.action === transitionStatus.action) {
      return { ...lastStepProgress, failureMessage };
    }
    return {
      action: transitionStatus.action,
      completedSteps: 0,
      totalSteps:
        getActionDefinition(workflowInfo, {
          state,
          action: transitionStatus.action,
          steps: [],
        })?.actionSteps.length ?? 0,
      failureMessage,
    };
  }

  const { transitionAction, succeededSteps, unprocessedSteps, failedSteps } =
    pendingTransition;
  const steps = [
    ...succeededSteps,
    ...unprocessedSteps,
    ...failedSteps.map(step => step.actionStep),
  ];
  const actionDefinition = getActionDefinition(workflowInfo, {
    state,
    action: transitionAction,
    steps,
  });

  const stepDisplayNames = new Map<string, string>();
  actionDefinition?.stepDisplayNames.forEach(({ key, value }) => {
    if (value?.trim() && !stepDisplayNames.has(key)) {
      stepDisplayNames.set(key, value);
    }
  });

  const orderedSteps = actionDefinition?.actionSteps ?? unprocessedSteps;
  const currentStepIndex = orderedSteps.findIndex(step =>
    unprocessedSteps.includes(step),
  );
  const currentStepDisplayName = orderedSteps
    .slice(0, currentStepIndex === -1 ? undefined : currentStepIndex + 1)
    .flatMap(step => stepDisplayNames.get(step) ?? [])
    .at(-1);

  let failureMessage: string | undefined;
  if (failedSteps.length) {
    failureMessage =
      failedSteps.flatMap(step => step.failuresMessages).join(', ') ||
      'A workflow step failed';
  } else if (transitionStatus?.failed) {
    failureMessage = transitionStatus.failed.error || 'The transition failed';
  }

  return {
    action: transitionAction,
    currentStepDisplayName,
    completedSteps: new Set(succeededSteps).size,
    totalSteps: new Set(steps).size,
    failureMessage,
  };
};
